import {Content, LoadingButton, useBreakPoints, useTranslation} from '@hconnect/uikit'
import {PageHeader} from '@hm/fundamental'
import {ArrowForward, Add as AddIcon} from '@mui/icons-material'
import {Box, Grid, List, Paper, Typography, Button} from '@mui/material'
import {useFormContext, useFieldArray} from 'react-hook-form'
import {useHistory} from 'react-router'

import {CustomerTier, CustomerTierSettings} from '../types/CustomerTierSettings'

import {CustomerTierListItem} from './CustomerTierListItem'
import {CustomerTierSettingsConstraints} from './CustomerTierSettingsConstraints'
import {useStyles} from './CustomerTierSettingsDetails.styles'

type Props = {
  mode: 'edit' | 'create' | 'copy'
  configId?: string
  customerTierSetting: CustomerTierSettings
  onSaveButtonClick: () => void
  isSubmitting: boolean
}

export const CustomerTierSettingsDetails = ({
  mode,
  customerTierSetting,
  onSaveButtonClick,
  isSubmitting
}: Props) => {
  const {t} = useTranslation()
  const screenSizes = useBreakPoints()
  const isMobile = ['xs', 'sm'].includes(screenSizes)
  const {classes} = useStyles({isMobile})
  const history = useHistory()
  const {watch, control} = useFormContext<CustomerTierSettings>()
  const config = watch()
  const isFormReady = Object.keys(config).length > 0

  const {fields, append, remove} = useFieldArray({
    control,
    name: 'tiers'
  })

  const formatTitle = () => {
    switch (mode) {
      case 'edit':
        return t('configurations.hconnect.customerTierSettings.configurationDetails.titleEdit')
      case 'create':
        return t('configurations.hconnect.customerTierSettings.configurationDetails.titleCreate')
      case 'copy':
        return t('configurations.hconnect.customerTierSettings.configurationDetails.titleCopy')
    }
  }

  const handleAddTier = () => {
    const existingNames = new Set(fields.map((field) => (field as CustomerTier).name))
    let newTierName = 'A'
    for (let charCode = 65; charCode <= 90; charCode++) {
      const potentialName = String.fromCharCode(charCode)
      if (!existingNames.has(potentialName)) {
        newTierName = potentialName
        break
      }
    }
    append({name: newTierName, percentage: 1, leadTimeInBusinessDays: null} as CustomerTier)
  }

  // Don't sort tiers - keep them in the order they were added so new tiers appear at the bottom
  const tiersForDisplay = [...fields]

  if (!customerTierSetting || !isFormReady) {
    return (
      <div>{t('configurations.hconnect.customerTierSettings.configurationDetails.loading')}</div>
    )
  }

  return (
    <Content>
      <div className="[&_*]:!text-white">
        <PageHeader
          data-cy={`customer-tier-settings-title-${mode}`}
          className="pl-0 pr-0 md:pl-0 md:pr-0"
          backToPageLabel={t(
            'configurations.hconnect.customerTierSettings.configurationDetails.goBack'
          )}
          backToPageUrlOnClick={() => history.goBack()}
        >
          {formatTitle()}
        </PageHeader>
      </div>
      <Grid container className={classes.gridContainer} spacing={2}>
        <Grid item lg={3} md={3} sm={12} xs={12} className={classes.gridItem}>
          <Paper elevation={4} className={classes.paper}>
            <Typography className={classes.cardTitle}>
              {t(
                'configurations.hconnect.customerTierSettings.configurationDetails.titleConstraints'
              )}
            </Typography>
            <CustomerTierSettingsConstraints disabled={mode === 'edit'} />
          </Paper>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12} className={classes.gridItem}>
          <Paper elevation={4} className={classes.paper}>
            <List>
              {tiersForDisplay.map((field) => {
                const originalIndex = fields.findIndex((f) => f.id === field.id)
                if (originalIndex === -1) return null

                return (
                  <CustomerTierListItem
                    key={field.id}
                    index={originalIndex}
                    onDelete={() => remove(originalIndex)}
                    totalTiers={fields.length}
                  />
                )
              })}
            </List>
            <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
              <Button
                startIcon={<AddIcon fontSize="small" />}
                variant="contained"
                color="primary"
                style={{background: 'rgb(1, 106, 212', textTransform: 'none'}}
                onClick={handleAddTier}
                data-test-id="add-customer-tier-button"
              >
                {t(
                  'configurations.hconnect.customerTierSettings.configurationDetails.buttonAddTier'
                )}
              </Button>
            </Box>
            <Box className={classes.saveButtonBox}>
              <LoadingButton
                startIcon={<ArrowForward fontSize="small" />}
                variant="outlined"
                color="primary"
                style={{background: 'rgb(1, 106, 212', textTransform: 'none'}}
                onClick={onSaveButtonClick}
                loading={isSubmitting}
                data-test-id="customer-tier-settings-save-button"
              >
                {mode === 'edit'
                  ? t(
                      'configurations.hconnect.customerTierSettings.configurationDetails.buttonSave'
                    )
                  : t(
                      `configurations.hconnect.customerTierSettings.configurationDetails.button${
                        mode.charAt(0).toUpperCase() + mode.slice(1)
                      }`
                    )}
              </LoadingButton>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Content>
  )
}
